import { Gender } from '../user/models/person';
import { DataResult } from '../http/httpDataResponse';
import { Fraction } from '../math/fraction';
import { TranslatableText } from '../i18n/translatable-text';

export type BookingMode = 'offering' | 'appointment';
export type DeletedByType = 'club' | 'member';
export type OfferingView = 'list' | 'timetable';

export type OfferingType = 'generic' | 'course' | 'exam' | 'training';

export enum OfferingPricingType {
  free = 'free',
  all = 'all',
  activeMembersPrice = 'active_members_price',
  passiveMembersPrice = 'passive_members_price',
  guestMembersPrice = 'guest_members_price',
  teamPrice = 'team_price',
  departmentPrice = 'department_price',
}

export type TrainingAppointmentResponse = 'accepted' | 'unsure' | 'rejected';

export enum Interval {
  daily = 'daily',
  weekly = 'weekly',
  biWeekly = 'biweekly',
  mondayToFriday = 'mondayToFriday',
  mondayToSaturday = 'mondayToSaturday',
  monthly = 'monthly',
  quarterly = 'quarterly',
  halfYearly = 'half-yearly',
  yearly = 'yearly',
}

export enum OfferingExecutionType {
  presence = 'presence',
  online = 'online',
  hybrid = 'hybrid',
}

export type OfferingTargetGroup = 'member' | 'guest' | 'all';

export enum BookingState {
  booked = 'booked',
  canceled = 'canceled',
  waitListWaiting = 'waitListWaiting',
  waitListCurrentlyBookable = 'waitListCurrentlyBookable',
  open = 'open',
}

export interface OfferingCategory {
  id: string;
  name: string;
  translatableName: TranslatableText;
  backgroundColor: string;
}

export interface Sport {
  id: string;
  name: TranslatableText;
}

export interface Instructor {
  id: number;
  fullName: string;
}

export interface LocationWithField extends Location {
  fieldId: string;
  fieldName: string;
  areaId: string;
  areaName: string;
}

export interface Location {
  id: string;
  name: string;
  street: string;
  addressCo?: string;
  city: string;
  postalCode: string;
  areas: LocationArea[];
}

export interface LocationArea {
  id: string;
  locationId: string;
  name: string;
  fields: LocationAreaField[];
}

export interface LocationAreaField {
  id: string;
  locationId: string;
  areaId: string;
  name: string;
}

export interface OfferingAppointmentOverride {
  title?: string;
  note?: string;
  isCanceled: boolean;
  instructors: Instructor[];
  locations: LocationWithField[];
}

export interface OfferingAppointment {
  isEditable?: boolean;
  canCreateBookings?: boolean;
  attendeesEnabled?: boolean;
  appointmentSeries: AppointmentSeries;
  bookingMode: BookingMode;
  start: string;
  end: string;
  bookingStart?: string;
  bookingEnd?: string;
  totalAvailablePlaces?: number;
  remainingAvailablePlaces?: number;
  hasAvailablePlaces: boolean;
  prices: OfferingPricing[];
  memberPrices: OfferingPricing[];
  offering: Offering;
  appointmentOverride?: OfferingAppointmentOverride;
  waitListActive: boolean;
  waitListEntries: WaitListEntry[];
  icsDownloadApiUrl: string;
  icsPreviewUrl?: string;
}

export interface WaitListEntry {
  offeringId?: string;
  appointmentSeriesId?: string;
  appointmentStart?: string;
  appointmentTitle?: string;
  offeringType?: OfferingType;
  id: string;
  member: OfferingMember;
  reservedByMember: OfferingMember;
  reservedUntil: string | null;
}

export interface AppointmentSeries {
  id: string;
  interval?: string | Interval;
  intervalStart?: string;
  intervalEnd?: string;
}

export interface BookableOfferingAppointment extends OfferingAppointment {
  isCancellable: boolean;
  bookings: OfferingBooking[];
  conditionsOfParticipationUrl: string;
}

export interface CourseOfferingAppointment extends BookableOfferingAppointment {
  offering: CourseOffering;
}

export interface ExamOfferingAppointment extends BookableOfferingAppointment {
  offering: ExamOffering;
}

export interface GenericOfferingAppointment extends BookableOfferingAppointment {
  offering: GenericOffering;
}

export interface TrainingOfferingAppointment extends OfferingAppointment {
  offering: TrainingOffering;
  replies: TrainingAppointmentReply[];
}

export interface OfferingBooking {
  member: OfferingMember;
  invoiceMember: OfferingMember;
  bookedByMember: OfferingMember;
  dog?: OfferingDog;
  clubExams?: ClubExam[];
  price: string;
  priceAsFraction: Fraction;
  bookingId?: string;
  createdAt?: string;
  deletedAt?: string;
  deletedBy?: DeletedByType;
  onlineUrl?: string;
  comment?: string;
  bookedSalesListOfferingItems?: BookedSalesListOfferingItem[];
  cancellationNote?: string;
}

export interface TrainingAppointmentReply {
  createdAt: string;
  member: OfferingMember;
  responseFromMember: OfferingMember;
  response: TrainingAppointmentResponse | null;
  comment?: string;
}

export interface OfferingPricing {
  money: string;
  moneyAsFraction: Fraction;
  currency: string;
  type: string | OfferingPricingType;
  memberId?: number;
  memberFullName?: string;
  dogs?: OfferingDog[];
}

export interface OfferingMember {
  id: number;
  fullName: string;
}

export interface OfferingDog {
  id: number;
  name: string;
}

export interface PricePerTime {
  data: {
    type: string;
    currency: string;
    netOrGross: 'gross' | 'net';
    net: DataResult<Fraction>;
    gross: DataResult<Fraction>;
    vat: DataResult<Fraction>;
  };
}

export interface SalesListOfferingItem {
  id: string;
  clubFeeId: string;
  clubFeeName: string;
  pricePerTime: PricePerTime;
}

export interface BookedSalesListOfferingItem extends Fraction {
  memberClubFeeId: string;
  clubFeeName: string;
  pricePerTime: PricePerTime;
}

export interface SelectedSalesListOfferingItem extends Fraction {
  id: string;
}

export interface Offering {
  id: string;
  customId: string;
  isFavorite: boolean;
  type: OfferingType;
  category: OfferingCategory;
  executionType: OfferingExecutionType;
  instructors: Instructor[];
  title: string;
  description?: string;
  minimumAge?: number;
  maximumAge?: number;
  minParticipants?: number;
  maxParticipants?: number;
  registrationRequested: boolean;
  showAvailablePlaces: boolean;
  bookableForMen: boolean;
  bookableForWomen: boolean;
  bookableForActiveMembers: boolean;
  bookableForPassiveMembers: boolean;
  bookableForGuestMembers: boolean;
  bookableDepartmentNames: string[];
  bookableTeamNames: string[];
  bookingStart?: string;
  bookingEnd?: string;
  bookingStartPerAppointmentStartDate?: string;
  locations: LocationWithField[];
  sport: TranslatableText;
  sportColor: string;
  requirements?: string;
  showCommentField: boolean;
  commentFieldCaption?: TranslatableText;
  hasImage: boolean;
  imageApiUrl: string;
  salesListOfferingItems: DataResult<SalesListOfferingItem>[];
}

export interface BookableOffering extends Offering {}

export interface CourseOffering extends BookableOffering {
  type: 'course';
}

export type GenderFilterValue = 'male' | 'female';

export type ExamineeType = 'member' | 'dog';

export interface ExamOffering extends BookableOffering {
  type: 'exam';
  examineeType: ExamineeType;
  clubExams: ClubExam[];
  selectableExams: boolean;
}

export interface ClubExam {
  id: string;
  title: string;
  examineeType?: ExamineeType;
}

export interface GenericOffering extends BookableOffering {
  type: 'generic';
}

export interface TrainingOffering extends Offering {
  type: 'training';
}

export type stateFilterValue = null | 'onlyBooked' | 'onlyFavorites';

export interface OfferingFiltersQueryParams {
  state?: stateFilterValue;
  query?: string | null;
  categoryIds?: string[] | string;
  sportIds?: string[] | string;
  departmentIds?: number[] | number;
  teamIds?: number[] | number;
  instructorIds?: string[] | string;
  locationIds?: string[] | string;
  executionType?: OfferingExecutionType.presence | OfferingExecutionType.online;
  weekdays?: string[] | string;
  startDateRange?: string; // JSON encoded OfferingFilterDateRange
  startTimeRange?: string; // JSON encoded OfferingFilterTimeRange
  free?: 'false' | 'true';
  gender?: GenderFilterValue;
  age?: string;
  targetGroup?: OfferingTargetGroup;
  [key: string]: any; // might contain additional parameters that should be ignored by filters
}

export interface OfferingFiltersApiQueryParams {
  start?: string;
  end?: string;
  'filters[teamIds][]'?: string[];
  'filters[categoryIds][]'?: string[];
  'filters[sportIds][]'?: string[];
  'filters[departmentIds][]'?: number[];
  'filters[instructorIds][]'?: number[];
  'filters[locationIds][]'?: string[];
  'filters[areaIds][]'?: string[];
  'filters[fieldIds][]'?: string[];
  'filters[weekdays][]'?: string[];
  'filters[executionTypes][]'?: OfferingExecutionType[];
  'filters[onlyBooked]'?: number;
  'filters[isFavorite]'?: number;
  'filters[free]'?: 1 | 0;
  'filters[startTimeRange][start][hours]'?: number;
  'filters[startTimeRange][start][minutes]'?: number;
  'filters[startTimeRange][end][hours]'?: number;
  'filters[startTimeRange][end][minutes]'?: number;
  'filters[forAge]'?: number;
  'filters[isBookableForMen]'?: 1 | 0;
  'filters[isBookableForWomen]'?: 1 | 0;
  'filters[isBookableForActiveOrPassiveMembers]'?: 1 | 0;
  'filters[isBookableForGuestMembers]'?: 1 | 0;
}

export interface OfferingFilters {
  query?: string | null;
  state?: stateFilterValue;
  teamIds?: string[];
  categoryIds?: string[];
  sportIds?: string[];
  departmentIds?: number[];
  instructorIds?: number[];
  locations?: Location[];
  areas?: LocationArea[];
  fields?: LocationAreaField[];
  executionType?: OfferingExecutionType.presence | OfferingExecutionType.online;
  weekdays?: string[];
  startDateRange?: OfferingFilterDateRange;
  startTimeRange?: OfferingFilterTimeRange;
  free?: boolean | null;
  onlyBooked?: boolean;
  isFavorite?: boolean;
  includeCanceled?: boolean;
  includePast?: boolean;
  includeWaitListed?: boolean;
  gender?: GenderFilterValue;
  age?: number;
  targetGroup?: OfferingTargetGroup;
}

export interface OfferingFiltersRouterParams extends Omit<Omit<OfferingFilters, 'startTimeRange'>, 'startDateRange'> {
  locationIds?: string[];
  areaIds?: string[];
  fieldIds?: string[];
  startDateRange?: string;
  startTimeRange?: string;
}

interface OfferingFilterDateRange {
  start: string;
  end: string;
}

interface OfferingFilterTimeRange {
  start: OfferingFilterTime;
  end: OfferingFilterTime;
}

interface OfferingFilterTime {
  hours: number;
  minutes: number;
}

export interface OfferingBookingGuestDto {
  firstName: string;
  lastName: string;
  gender: Gender;
  email?: string;
  dateOfBirth: string;
  emergencyContact: string;
}

export type categoryName = 'course' | 'exam';
